import config from '../config';
import { supabase } from '../lib/supabase';

/**
 * Live Stream Detection Service
 *
 * Zero-quota approach:
 * 1. Resolve @handle → UCxxx channel ID via proxied channel page (cached in localStorage)
 * 2. Fetch YouTube RSS feed via Netlify proxy (free, no API key)
 * 3. Parse latest video ID from XML
 * 4. Use videos.list (1 API unit) to check liveBroadcastContent
 * 5. Update is_live_now in Supabase
 *
 * Total cost: 1 API unit per check. Called only during active events.
 */

const CHANNEL_ID_CACHE_KEY = 'yt_channel_ids';

/**
 * Extract @handle from a YouTube channel URL
 */
function extractHandle(url) {
  if (!url) return null;
  const match = url.match(/@([^/?\s]+)/);
  return match ? match[1] : null;
}

/**
 * Resolve @handle to UCxxx channel ID.
 * Fetches the channel page via Netlify proxy and extracts the canonical channel ID.
 * Result is cached permanently in localStorage.
 */
async function resolveChannelId(youtubeChannelUrl) {
  if (!youtubeChannelUrl) return null;

  // If already a channel ID URL, extract directly
  const channelIdMatch = youtubeChannelUrl.match(/\/channel\/(UC[a-zA-Z0-9_-]+)/);
  if (channelIdMatch) return channelIdMatch[1];

  const handle = extractHandle(youtubeChannelUrl);
  if (!handle) return null;

  // Check localStorage cache
  try {
    const cached = JSON.parse(localStorage.getItem(CHANNEL_ID_CACHE_KEY) || '{}');
    if (cached[handle]) return cached[handle];
  } catch (e) {}

  // Fetch channel page via proxy to extract channel ID
  try {
    const resp = await fetch(`/api/youtube-channel/${handle}`);
    if (!resp.ok) return null;
    const html = await resp.text();

    // Extract channel ID from canonical link or meta tag
    // Pattern: <link rel="canonical" href="https://www.youtube.com/channel/UCxxxxxxx">
    const canonicalMatch = html.match(/\/channel\/(UC[a-zA-Z0-9_-]+)/);
    if (!canonicalMatch) return null;

    const channelId = canonicalMatch[1];

    // Cache permanently
    try {
      const cached = JSON.parse(localStorage.getItem(CHANNEL_ID_CACHE_KEY) || '{}');
      cached[handle] = channelId;
      localStorage.setItem(CHANNEL_ID_CACHE_KEY, JSON.stringify(cached));
    } catch (e) {}

    return channelId;
  } catch (e) {
    console.error('[LiveDetection] Failed to resolve channel ID:', e);
    return null;
  }
}

/**
 * Fetch the latest video IDs from YouTube RSS feed (free, no API quota).
 * Returns array of { videoId, title, published } for the latest entries.
 */
async function fetchLatestFromRSS(channelId) {
  try {
    const resp = await fetch(`/api/youtube-rss/${channelId}`);
    if (!resp.ok) return [];

    const xml = await resp.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');
    const entries = doc.querySelectorAll('entry');

    const results = [];
    // Check latest 5 entries (live streams appear as recent entries)
    for (let i = 0; i < Math.min(5, entries.length); i++) {
      const entry = entries[i];
      const videoId = entry.querySelector('videoId')?.textContent;
      const title = entry.querySelector('title')?.textContent;
      const published = entry.querySelector('published')?.textContent;
      if (videoId) results.push({ videoId, title, published });
    }

    return results;
  } catch (e) {
    console.error('[LiveDetection] RSS fetch failed:', e);
    return [];
  }
}

/**
 * Check if specific video IDs are currently live using YouTube Data API.
 * Cost: 1 API unit per call (videos.list).
 */
async function checkVideosLiveStatus(videoIds) {
  const apiKey = config.GOOGLE_API_KEY;
  if (!apiKey || videoIds.length === 0) return [];

  try {
    const resp = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?` +
      `part=snippet,liveStreamingDetails&id=${videoIds.join(',')}&key=${apiKey}`
    );
    if (!resp.ok) return [];

    const data = await resp.json();
    return (data.items || []).map(item => ({
      videoId: item.id,
      title: item.snippet?.title,
      channelTitle: item.snippet?.channelTitle,
      thumbnail: item.snippet?.thumbnails?.high?.url,
      isLive: item.snippet?.liveBroadcastContent === 'live',
      isUpcoming: item.snippet?.liveBroadcastContent === 'upcoming',
      publishedAt: item.snippet?.publishedAt,
      actualStartTime: item.liveStreamingDetails?.actualStartTime,
    }));
  } catch (e) {
    console.error('[LiveDetection] videos.list failed:', e);
    return [];
  }
}

/**
 * Main entry point: check if a center's YouTube channel is currently live.
 *
 * @param {string} centerId - Supabase center ID
 * @param {string} youtubeChannelUrl - YouTube channel URL (e.g. https://youtube.com/@handle)
 * @returns {{ isLive, videoId, title, thumbnail }} or null
 */
export async function checkCenterLiveStatus(centerId, youtubeChannelUrl) {
  console.log('[LiveDetection] Checking live status for center:', centerId);

  // Coordination: If ANY client already ran detection for this center recently,
  // skip the YouTube API call and return the current DB state instead.
  // This ensures only ~1 API call per 8 minutes regardless of how many clients are open.
  const COORDINATION_WINDOW_MS = 8 * 60 * 1000; // 8 minutes
  try {
    const recentThreshold = new Date(Date.now() - COORDINATION_WINDOW_MS).toISOString();
    const { data: recentLive } = await supabase
      .from('videos')
      .select('youtube_video_id, title, thumbnail_url, is_live_now, updated_at')
      .eq('center_id', centerId)
      .eq('video_type', 'live')
      .gte('updated_at', recentThreshold)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (recentLive) {
      console.log('[LiveDetection] Recent check found (within 8 min), using DB state');
      if (recentLive.is_live_now) {
        return {
          isLive: true,
          videoId: recentLive.youtube_video_id,
          title: recentLive.title,
          thumbnail: recentLive.thumbnail_url,
        };
      }
      return { isLive: false };
    }
  } catch (e) {
    // Coordination check failed — proceed with normal detection
    console.warn('[LiveDetection] Coordination check failed, proceeding:', e);
  }

  // Step 1: Resolve channel ID (cached after first call)
  const channelId = await resolveChannelId(youtubeChannelUrl);
  if (!channelId) {
    console.log('[LiveDetection] Could not resolve channel ID');
    return null;
  }

  // Step 2: Get latest videos from RSS (free)
  const latestVideos = await fetchLatestFromRSS(channelId);
  if (latestVideos.length === 0) {
    console.log('[LiveDetection] No videos in RSS feed');
    return null;
  }

  // Step 3: Check live status via YouTube API (1 unit)
  const videoIds = latestVideos.map(v => v.videoId);
  const statuses = await checkVideosLiveStatus(videoIds);

  const liveVideo = statuses.find(v => v.isLive);
  const upcomingVideo = statuses.find(v => v.isUpcoming);

  // Step 4: Update Supabase
  if (liveVideo) {
    console.log('[LiveDetection] LIVE:', liveVideo.title);
    await upsertLiveVideo(centerId, liveVideo, true);
    return {
      isLive: true,
      videoId: liveVideo.videoId,
      title: liveVideo.title,
      thumbnail: liveVideo.thumbnail,
    };
  }

  // No live stream found — clear any stale is_live_now flags
  await clearStaleLiveFlags(centerId);

  if (upcomingVideo) {
    console.log('[LiveDetection] Upcoming stream:', upcomingVideo.title);
    return { isLive: false, isUpcoming: true, title: upcomingVideo.title };
  }

  console.log('[LiveDetection] No live stream detected');
  return { isLive: false };
}

/**
 * Upsert a live video into the videos table
 */
async function upsertLiveVideo(centerId, video, isLiveNow) {
  try {
    const { error } = await supabase
      .from('videos')
      .upsert({
        youtube_video_id: video.videoId,
        center_id: centerId,
        title: video.title,
        video_url: `https://www.youtube.com/watch?v=${video.videoId}`,
        video_type: 'live',
        privacy_status: 'public',
        published_at: video.publishedAt || new Date().toISOString(),
        thumbnail_url: video.thumbnail,
        is_live_now: isLiveNow,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'youtube_video_id' });

    if (error) console.error('[LiveDetection] Upsert error:', error);
  } catch (e) {
    console.error('[LiveDetection] Upsert failed:', e);
  }
}

/**
 * Clear stale is_live_now flags for a center
 */
async function clearStaleLiveFlags(centerId) {
  try {
    await supabase
      .from('videos')
      .update({ is_live_now: false, updated_at: new Date().toISOString() })
      .eq('center_id', centerId)
      .eq('is_live_now', true);
  } catch (e) {
    console.error('[LiveDetection] Clear stale flags failed:', e);
  }
}
